// EditGroup.js
import PageContainer from "../PageContainer";
import { GroupForm } from "./GroupForm";
import { useData } from "../../../contexts/DataContext/DataContextt";
import { editGroup, fetchGroups } from "../../../services/groupService";
import { useMemo, useState } from "react";
import { usePreload } from "../../../contexts/DataContext/PreloadContext";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { groupsPageCache } from "../../../pages/cache/PageCache";
import { usePagedQuery } from "../../../contexts/hooks/usePagedQuery";
import { Stack, Tooltip, IconButton, Chip } from "@mui/material";
import { localizeField } from "../../../config/localization";

const EditGroup = () => {
  const { loading, language } = useData();
  const { showNotification } = usePreload();
  const location = useLocation();
  const { id: groupCode } = useParams();
  const [updating, setUpdating] = useState(false);
  const {
    mutate,
    data: rows,
    fetched,
  } = usePagedQuery({
    fetchFunction: fetchGroups,
    language: "all",
    searchOption: [{ id: "groupCode", display: "Code" }],
    cache: groupsPageCache,
    deps: [language],
    searchKey: groupCode,
  });

  // Router state is only present when coming from the list; fall back to the fetched row
  const row = useMemo(() => {
    return {
      ...(location.state?.row ?? rows[0]),
      groupName: localizeField(rows, "groupName"),
      groupDisplayName: localizeField(rows, "groupDisplayName"),
    };
  }, [rows, location.state]);

  if (fetched && !rows[0]?.groupCode) {
    return <Navigate to="/notfound" replace />;
  }

  const onUpdate = async (updatedRow) => {
    try {
      setUpdating(true);
      //let newGroup = keepOnlyCurrentLanguage(updatedRow, language);
      let newGroup = updatedRow;
      newGroup = {
        ...newGroup,
        groupClasses: Array.isArray(newGroup.groupClasses)
          ? newGroup.groupClasses.join("|")
          : newGroup.groupClasses || "",
      };

      // apiRequest hands back the raw Response on a non-2xx instead of throwing
      const response = await editGroup(newGroup);
      if (response instanceof Response && !response.ok) {
        throw new Error(`Edit group failed with status ${response.status}`);
      }
      mutate(
        {
          ...newGroup,
          id: newGroup.groupCode,
          groupName: newGroup.groupName[language] ?? "",
          groupDisplayName: newGroup.groupDisplayName[language] ?? "",
        },
        "groupCode",
      );

      showNotification(`${updatedRow?.groupCode} was updated!`, `success`);
      return true;
    } catch (e) {
      console.error(e);
      showNotification(
        `something went wrong updating the ${updatedRow?.groupCode} group.`,
        "error",
      );
      return false;
    } finally {
      setUpdating(false);
    }
  };

  return (
    <PageContainer
      title="Edit Group"
      subtitle="Update names and review the classes linked to this group"
      titleAdornment={<Chip size="small" label={groupCode} />}
      breadcrumbs={[
        { title: "ETIM Groups", path: "/groups" },
        { title: groupCode },
      ]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Back" placement="bottom" enterDelay={300}>
            {
              <div>
                {/* a Link so GroupForm's unsaved-changes guard covers it */}
                <IconButton
                  size="small"
                  aria-label="Back to groups"
                  component={Link}
                  to="/groups"
                >
                  <KeyboardBackspaceIcon />
                </IconButton>
              </div>
            }
          </Tooltip>
        </Stack>
      }
    >
      <GroupForm
        onSave={onUpdate}
        props={{
          row: row,
          loading: loading,
          uploading: updating,
          showCancel: false,
          saveText: "Save Changes",
          isUpdate: true,
        }}
      />
    </PageContainer>
  );
};

export default EditGroup;
